import { useEffect, useState, useCallback } from "react";
import { Search, Eye, Download, Printer, Trash2, Loader2, FolderOpen } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { canEmbedPdf } from "../lib/pdf";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "../components/ui/alert-dialog";

const TYPE_FILTERS = [
  { value: "tous", label: "Tous", testid: "filter-type-all" },
  { value: "professionnel", label: "Professionnel", testid: "filter-type-pro" },
  { value: "personnel", label: "Personnel", testid: "filter-type-personal" },
];

export default function Contracts() {
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [type, setType] = useState("tous");
  const [period, setPeriod] = useState("tous");
  const [viewing, setViewing] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/contracts", { params: { search, type, period } });
      setContracts(data);
    } catch (e) {
      toast.error(formatApiError(e, "Impossible de charger les contrats"));
    } finally {
      setLoading(false);
    }
  }, [search, type, period]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  const fileUrl = (c, format) =>
    `${process.env.REACT_APP_BACKEND_URL}/api/contracts/${c.contract_id}/file?format=${format}`;

  const printContract = (c) => {
    const url = fileUrl(c, "pdf");
    const iframe = document.createElement("iframe");
    iframe.style.position = "fixed";
    iframe.style.right = "0";
    iframe.style.bottom = "0";
    iframe.style.width = "0";
    iframe.style.height = "0";
    iframe.style.border = "0";
    iframe.src = url;
    const cleanup = () => {
      if (iframe.parentNode) iframe.parentNode.removeChild(iframe);
    };
    iframe.onload = () => {
      try {
        iframe.contentWindow.onafterprint = cleanup;
        iframe.contentWindow.print();
      } catch {
        window.open(url, "_blank");
        cleanup();
      }
      setTimeout(cleanup, 60000);
    };
    document.body.appendChild(iframe);
  };

  const confirmDelete = async () => {
    if (!deleting) return;
    setDeleteLoading(true);
    try {
      await api.delete(`/contracts/${deleting.contract_id}`);
      toast.success(`Contrat de ${deleting.nom_complet} supprimé définitivement.`);
      setDeleting(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e, "Suppression impossible"));
    } finally {
      setDeleteLoading(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="contracts-page">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 flex items-center gap-3">
          <FolderOpen className="h-7 w-7 text-slate-700" /> Anciens contrats
        </h1>
        <p className="text-sm text-slate-500 mt-1">Retrouvez, téléchargez, imprimez ou supprimez vos contrats.</p>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm flex flex-col lg:flex-row gap-4 lg:items-center">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input className="pl-9" placeholder="Rechercher : locataire, propriétaire, N° de contrat, pièce d'identité, téléphone, adresse, date…"
            value={search} onChange={(e) => setSearch(e.target.value)} data-testid="contracts-search-input" />
        </div>
        <div className="flex rounded-lg border border-slate-200 overflow-hidden">
          {TYPE_FILTERS.map((f) => (
            <button key={f.value} onClick={() => setType(f.value)} data-testid={f.testid}
              className={`px-4 py-2 text-sm font-semibold transition-colors duration-150 ${
                type === f.value ? "bg-slate-900 text-white" : "bg-white text-slate-600 hover:bg-slate-50"
              }`}>
              {f.label}
            </button>
          ))}
        </div>
        <Select value={period} onValueChange={setPeriod}>
          <SelectTrigger className="w-full lg:w-48" data-testid="filter-date-select">
            <SelectValue placeholder="Période" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="tous" data-testid="filter-date-all">Toutes les dates</SelectItem>
            <SelectItem value="jour" data-testid="filter-date-today">Aujourd'hui</SelectItem>
            <SelectItem value="semaine" data-testid="filter-date-week">Cette semaine</SelectItem>
            <SelectItem value="mois" data-testid="filter-date-month">Ce mois</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-16" data-testid="contracts-loading">
            <Loader2 className="h-7 w-7 animate-spin text-blue-600" />
          </div>
        ) : contracts.length === 0 ? (
          <p className="py-16 text-center text-sm text-slate-500" data-testid="contracts-empty">
            Aucun contrat trouvé. Modifiez votre recherche ou générez un nouveau contrat.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="contracts-table">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50 text-left">
                  <th className="px-4 py-3 text-xs font-medium text-slate-500 uppercase tracking-wider">N° contrat</th>
                  <th className="px-4 py-3 text-xs font-medium text-slate-500 uppercase tracking-wider">Locataire</th>
                  <th className="px-4 py-3 text-xs font-medium text-slate-500 uppercase tracking-wider hidden md:table-cell">Propriétaire</th>
                  <th className="px-4 py-3 text-xs font-medium text-slate-500 uppercase tracking-wider">Type</th>
                  <th className="px-4 py-3 text-xs font-medium text-slate-500 uppercase tracking-wider hidden lg:table-cell">Bien / Adresse</th>
                  <th className="px-4 py-3 text-xs font-medium text-slate-500 uppercase tracking-wider hidden md:table-cell">Montant</th>
                  <th className="px-4 py-3 text-xs font-medium text-slate-500 uppercase tracking-wider hidden lg:table-cell">Début</th>
                  <th className="px-4 py-3 text-xs font-medium text-slate-500 uppercase tracking-wider hidden lg:table-cell">Fin</th>
                  <th className="px-4 py-3 text-xs font-medium text-slate-500 uppercase tracking-wider hidden xl:table-cell">Créé le</th>
                  <th className="px-4 py-3 text-xs font-medium text-slate-500 uppercase tracking-wider text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {contracts.map((c) => (
                  <tr key={c.contract_id} className="hover:bg-slate-50 transition-colors duration-100" data-testid={`contract-row-${c.contract_id}`}>
                    <td className="px-4 py-3.5 text-xs font-mono font-semibold text-slate-700 whitespace-nowrap" data-testid={`contract-numero-${c.contract_id}`}>
                      {c.numero_contrat || c.contract_id}
                    </td>
                    <td className="px-4 py-3.5 font-semibold text-slate-900 whitespace-nowrap">{c.nom_complet}</td>
                    <td className="px-4 py-3.5 text-slate-600 whitespace-nowrap hidden md:table-cell">{c.owner_nom_complet || "—"}</td>
                    <td className="px-4 py-3.5">
                      <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${
                        c.type === "professionnel" ? "bg-blue-50 text-blue-700" : "bg-emerald-50 text-emerald-700"
                      }`}>
                        {c.type === "professionnel" ? "Professionnel" : "Personnel"}
                      </span>
                    </td>
                    <td className="px-4 py-3.5 text-xs text-slate-600 hidden lg:table-cell max-w-44 truncate">{c.designation || c.adresse || "—"}</td>
                    <td className="px-4 py-3.5 text-xs font-semibold text-slate-700 whitespace-nowrap hidden md:table-cell">{c.montant ? `${c.montant} FCFA` : "—"}</td>
                    <td className="px-4 py-3.5 text-xs text-slate-600 whitespace-nowrap hidden lg:table-cell">{c.date_debut || "—"}</td>
                    <td className="px-4 py-3.5 text-xs text-slate-600 whitespace-nowrap hidden lg:table-cell">{c.date_fin || "—"}</td>
                    <td className="px-4 py-3.5 text-xs text-slate-600 whitespace-nowrap hidden xl:table-cell">
                      {new Date(c.created_at).toLocaleDateString("fr-FR")}
                      <span className="text-slate-400 ml-1">{new Date(c.created_at).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}</span>
                    </td>
                    <td className="px-4 py-3.5">
                      <div className="flex items-center justify-end gap-1">
                        <button title="Voir" onClick={() => setViewing(c)} data-testid={`contract-action-view-${c.contract_id}`}
                          className="p-2 rounded-lg text-slate-500 hover:bg-blue-50 hover:text-blue-600 transition-colors duration-150">
                          <Eye className="h-4 w-4" />
                        </button>
                        <a title="Télécharger PDF" href={fileUrl(c, "pdf")} download data-testid={`contract-action-pdf-${c.contract_id}`}
                          className={`p-2 rounded-lg transition-colors duration-150 ${c.pdf_file ? "text-slate-500 hover:bg-slate-100 hover:text-slate-900" : "text-slate-300 pointer-events-none"}`}>
                          <Download className="h-4 w-4" />
                        </a>
                        <a title="Télécharger Word" href={fileUrl(c, "docx")} download data-testid={`contract-action-docx-${c.contract_id}`}
                          className="p-2 rounded-lg text-slate-500 hover:bg-slate-100 hover:text-slate-900 transition-colors duration-150">
                          <span className="text-xs font-bold">W</span>
                        </a>
                        <button title="Imprimer" onClick={() => printContract(c)} disabled={!c.pdf_file} data-testid={`contract-action-print-${c.contract_id}`}
                          className="p-2 rounded-lg text-slate-500 hover:bg-slate-100 hover:text-slate-900 transition-colors duration-150 disabled:text-slate-300">
                          <Printer className="h-4 w-4" />
                        </button>
                        <button title="Supprimer" onClick={() => setDeleting(c)} data-testid={`contract-action-delete-${c.contract_id}`}
                          className="p-2 rounded-lg text-slate-500 hover:bg-rose-50 hover:text-rose-600 transition-colors duration-150">
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Dialog open={!!viewing} onOpenChange={(o) => !o && setViewing(null)}>
        <DialogContent className="max-w-4xl h-[85vh] flex flex-col" data-testid="contract-view-dialog">
          <DialogHeader>
            <DialogTitle>{viewing?.nom_complet} — {viewing?.type === "professionnel" ? "Contrat professionnel" : "Contrat personnel"}</DialogTitle>
          </DialogHeader>
          {viewing?.pdf_file && canEmbedPdf() ? (
            <iframe src={fileUrl(viewing, "pdf")} title="Contrat" className="flex-1 w-full rounded-lg bg-slate-100" data-testid="contract-view-iframe" />
          ) : viewing?.pdf_file ? (
            <div className="flex-1 flex flex-col items-center justify-center gap-4" data-testid="contract-view-fallback">
              <p className="text-sm text-slate-500">L'aperçu intégré n'est pas disponible sur cet appareil.</p>
              <a href={fileUrl(viewing, "pdf")} target="_blank" rel="noopener noreferrer" data-testid="contract-view-open-pdf-btn"
                className="inline-flex items-center gap-2 bg-slate-900 text-white text-sm font-semibold px-5 py-2.5 rounded-lg hover:bg-slate-800 transition-colors duration-150">
                Ouvrir le contrat (PDF)
              </a>
            </div>
          ) : (
            <p className="text-sm text-slate-500 py-10 text-center">Aperçu PDF indisponible. Téléchargez le fichier Word.</p>
          )}
          <div className="flex gap-3 pt-2">
            <a href={viewing ? fileUrl(viewing, "pdf") : "#"} download className="text-sm font-semibold text-blue-600 hover:underline" data-testid="contract-view-download-pdf">Télécharger PDF</a>
            <a href={viewing ? fileUrl(viewing, "docx") : "#"} download className="text-sm font-semibold text-blue-600 hover:underline" data-testid="contract-view-download-docx">Télécharger Word</a>
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleting} onOpenChange={(o) => !o && setDeleting(null)}>
        <AlertDialogContent data-testid="contract-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer ce contrat ?</AlertDialogTitle>
            <AlertDialogDescription>
              Le contrat de <strong>{deleting?.nom_complet}</strong> ({deleting?.filename_base}) sera définitivement supprimé,
              ainsi que les fichiers associés. Cette action est irréversible.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="contract-delete-cancel-btn">Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} disabled={deleteLoading} data-testid="contract-delete-confirm-btn"
              className="bg-rose-600 hover:bg-rose-700">
              {deleteLoading && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              Supprimer définitivement
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
